import { useEffect, useRef, useState } from 'react';
import { CircleHelp, Gauge, Keyboard, LogOut, Moon, Palette, Settings2, Sun } from 'lucide-react';
import type { PaigeWorkbenchPerson } from '../paige/paige.global';
import { openShortcutSheet } from '../paige/host-shortcuts';
import { workbenchPerson } from './WorkbenchPerson';
import './account-door.css';

/** Opens the workbench's Appearance page on its own (the App listens). */
export const OPEN_APPEARANCE_EVENT = 'paige-open-appearance';

/** "Dustin Baker" to "DB": the mark the dashboard's profile shows too. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? [words[0]![0], words[words.length - 1]![0]] : [name.trim()[0]];
  return letters.join('').toUpperCase();
}

/**
 * Paige's account door, where CapturePoint's status bar has This computer (Dustin, 2026-09-24): in
 * Paige the settings that matter are the person's and the company's, not the machine's. It replaces
 * the workspace menu; the dashboard is reached from the job door and learning from AIQ, as in
 * CapturePoint. Outside Paige there is nobody to name, and it renders nothing.
 */
export function AccountDoorButton() {
  const person = workbenchPerson();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    window.addEventListener('pointerdown', away);
    window.addEventListener('keydown', escape);
    return () => {
      window.removeEventListener('pointerdown', away);
      window.removeEventListener('keydown', escape);
    };
  }, [open]);
  if (!person) return null;
  return (
    <>
      <AdminMark person={person} />
      <div className="account-door" ref={root}>
        <button
          type="button"
          className="account-door-button"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
        >
          <span className="account-door-mark" aria-hidden="true">
            {initialsOf(person.name)}
          </span>
          <span>{person.company}</span>
        </button>
        {open && <AccountPanel person={person} onDone={() => setOpen(false)} />}
      </div>
      <ShortcutsMark />
    </>
  );
}

/**
 * The F1 sheet in plain sight (gap list C8; Dustin, 2026-10-20): nothing on the screen said it was there. A
 * keyboard beside the account door opens it; its tooltip names the key, so the second time is F1.
 */
function ShortcutsMark() {
  return (
    <button
      type="button"
      className="account-door-keys"
      aria-label="Keyboard shortcuts"
      title="Keyboard shortcuts (F1)"
      data-telemetry="shortcuts-button"
      onClick={openShortcutSheet}
    >
      <Keyboard size={16} aria-hidden="true" />
    </button>
  );
}

/**
 * The admin area in plain sight for Ademero staff on a job's own page, as the jobs page, settings and the plan
 * page have it (the web app's AdminMark; Dustin, 2026-10-07): a gauge and the word, named "Admin area". The host
 * hands the way there only to staff; anyone else is drawn nothing.
 */
function AdminMark({ person }: { person: PaigeWorkbenchPerson }) {
  if (!person.openAdmin) return null;
  return (
    <button
      type="button"
      className="account-door-admin"
      aria-label="Admin area"
      title="Admin area"
      onClick={person.openAdmin}
    >
      <Gauge size={16} aria-hidden="true" />
      <span>Admin</span>
    </button>
  );
}

/** The panel: who and where, light or dark, Appearance, the company's settings, Help, Sign out. */
function AccountPanel({ person, onDone }: { person: PaigeWorkbenchPerson; onDone: () => void }) {
  const run = (action: (() => void) | undefined) => () => {
    onDone();
    action?.();
  };
  return (
    <div className="account-door-panel" role="menu" aria-label="Your account">
      <div className="account-door-who">
        <strong>{person.name}</strong>
        <span>{person.company}</span>
      </div>
      {person.setTheme && (
        <div className="account-door-theme" role="group" aria-label="Theme">
          {(['light', 'dark'] as const).map((theme) => (
            <button
              key={theme}
              type="button"
              aria-label={theme === 'light' ? 'Light theme' : 'Dark theme'}
              aria-pressed={person.theme === theme}
              onClick={() => person.setTheme?.(theme)}
            >
              {theme === 'light' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          ))}
        </div>
      )}
      <button
        type="button"
        role="menuitem"
        onClick={run(() => window.dispatchEvent(new Event(OPEN_APPEARANCE_EVENT)))}
      >
        <Palette size={18} aria-hidden="true" />
        <span>Appearance</span>
      </button>
      {person.openSettings && (
        <button type="button" role="menuitem" onClick={run(person.openSettings)}>
          <Settings2 size={18} aria-hidden="true" />
          <span>Company settings</span>
        </button>
      )}
      {person.openHelp && (
        <button type="button" role="menuitem" onClick={run(person.openHelp)}>
          <CircleHelp size={18} aria-hidden="true" />
          <span>Help</span>
        </button>
      )}
      <button
        type="button"
        role="menuitem"
        aria-label={`Sign out ${person.name}`}
        onClick={run(person.signOut)}
      >
        <LogOut size={18} aria-hidden="true" />
        <span>Sign out</span>
      </button>
    </div>
  );
}
